// XXX this entire file can become obsolete if Kobol starts returning an already merged data set.
import {
  IExpressionColumnFieldsFragment,
  IExpressionSliceFieldsFragment,
  ISliceNameHint,
} from '../apollo/graphqlSchema';
import moment from 'moment';
import {
  COMPARISON_KEY_SUFFIX,
  COMPARISON_OVERALL_SLICE_NAME,
  INTERNAL_DATE_FORMAT,
} from '../../config/constants';
import { assertNotNil } from '../types';
import { IExpressionAnalysisData } from '../apollo/responseTypes';
import { isNil } from 'lodash';
import { getExpressionResultMeasureColumn } from './transformExpressionData';

// map a comparison (slice or column) key to a "comparison" one - with appropriate suffix
export const mapComparisonKey = (key: string) =>
  `${key}${COMPARISON_KEY_SUFFIX}`;

// map a comparison (slice or column) name to a "comparison" one - with appropriate human-friendly prefix
export const mapComparisonName = (name: string, future: boolean) =>
  `${future ? '[Comparison]' : '[Previous]'} ${name}`;

// retrieve an item from a record and also delete it from the record if found
const getAndRemove = <T>(rec: Record<string, T>, key: string): T | null => {
  const ret = rec[key];
  if (ret !== undefined) {
    delete rec[key];
  }
  return ret ?? null; // convert undefined to null
};

// determine if our comparison dates end after or before the primary date -
// affects whether comparison names say 'Previous' vs 'Comparison'
const isFutureComparison = (dates: string[], cDates: string[]): boolean => {
  const lastDate =
    dates.length > 0
      ? moment(dates[dates.length - 1], INTERNAL_DATE_FORMAT)
      : moment();
  const comparisonLastDate =
    cDates.length > 0
      ? moment(cDates[cDates.length - 1], INTERNAL_DATE_FORMAT)
      : lastDate;
  return comparisonLastDate.isAfter(lastDate);
};

// An expression column with two extra props - to keep track of
// whether the column is comparison and its index in the original columns list.
export interface IComparisonMergeExpressionColumn
  extends IExpressionColumnFieldsFragment {
  isComparison: boolean;
  isFutureComparison: boolean;
  // only "null" (generated by Caprica) columns can have this to be null
  originalIndex: number | null;
  originalSliceKey: string;
}

// An expression slice with an extra flag
interface IMergedExpressionSlice extends IExpressionSliceFieldsFragment {
  isComparison: boolean;
  isNullSlice: boolean;
  originalSliceKey: string;
}

// special (otherwise invalid) value for a "null"-slice variables index
const NULL_SLICE_COLUMN_INDEX = -1;

// map a comparison column key and name with respective suffix/prefix.
// the resulting slice is not subject to aliasing.
const mapComparisonSlice = (
  slice: IExpressionSliceFieldsFragment,
  future: boolean,
): IMergedExpressionSlice => {
  return {
    ...slice,
    key: mapComparisonKey(slice.key),
    name: mapComparisonName(slice.name, future),
    hint: slice.hint.map((h): ISliceNameHint => {
      return {
        ...h,
        value: mapComparisonName(h.value, future),
      };
    }),
    userAlias: { __typename: 'UserAliasSliceNone' },
    isComparison: true,
    isNullSlice: false,
    originalSliceKey: slice.key,
  };
};

// map a comparison column key and name(s) with respective suffix/prefixes
const mapComparisonColumn = (
  column: IExpressionColumnFieldsFragment,
  future: boolean,
): IExpressionColumnFieldsFragment => {
  // TODO handle readings?
  return {
    ...column,
    cumulativeSum: {
      ...column.cumulativeSum,
      display: mapComparisonName(column.cumulativeSum.display, future),
    },
    key: mapComparisonKey(column.key),
    name: mapComparisonName(column.name, future),
  };
};

// helpers to group all primary and comparison slices into pairs
// under a Record keyed by the (original) slice key. Note that some pairs
// can have only one of these present (but both can't be null)
interface ISlicesPair {
  primary: IExpressionSliceFieldsFragment | null;
  comparison: IExpressionSliceFieldsFragment | null;
}

const getSlicePairsByKey = (
  slices: IExpressionSliceFieldsFragment[],
  cSlices: IExpressionSliceFieldsFragment[],
): Record<string, ISlicesPair> => {
  const comparisonByKey: Record<string, IExpressionSliceFieldsFragment> = {};
  cSlices.forEach((c) => (comparisonByKey[c.key] = c));
  const ret: Record<string, ISlicesPair> = {};
  slices.forEach((c) => {
    const cc = getAndRemove(comparisonByKey, c.key);
    ret[c.key] = {
      primary: c,
      comparison: cc,
    };
  });
  // add column pairs for comparison columns not found in the primary
  for (const k of Object.keys(comparisonByKey)) {
    const cc = comparisonByKey[k];
    ret[k] = {
      primary: null,
      comparison: cc,
    };
  }
  return ret;
};

// generate a "null copy" of a slice. the resulting slice has invalid
// measure and variables - these are filled later when the final column indexes are known.
const nullSlice = (
  other: IExpressionSliceFieldsFragment,
): IExpressionSliceFieldsFragment => {
  return {
    ...other,
    resultMeasureColumnIndex: NULL_SLICE_COLUMN_INDEX,
    variables: other.variables.map(() => NULL_SLICE_COLUMN_INDEX),
  };
};

// generate a "null copy" of a column which has zeroes as values
const nullColumn = (
  primary: IExpressionColumnFieldsFragment,
): IExpressionColumnFieldsFragment => {
  return {
    __typename: 'ExpressionColumn',
    basePercents: primary.basePercents.map((x) => (x == null ? null : 0.0)),
    percentsPreviousValue: primary.percentsPreviousValue.map((x) =>
      x == null ? null : 0.0,
    ),
    cumulativeSum: {
      __typename: 'ExpressionCumulativeSum',
      display: primary.cumulativeSum.display,
      values: [],
    },
    key: primary.key,
    measureKey: primary.measureKey,
    name: primary.name,
    reading: null,
    values: primary.values.map(() => 0.0),
  };
};

const mergeSlices = (
  base: IExpressionSliceFieldsFragment | null,
  cBase: IExpressionSliceFieldsFragment | null,
  slices: IExpressionSliceFieldsFragment[],
  cSlices: IExpressionSliceFieldsFragment[],
  future: boolean,
): IMergedExpressionSlice[] => {
  const ret: IMergedExpressionSlice[] = [];
  // XXX replace 'Overall' name with "Measure" when the query has no breakdowns
  // and thus - no "Overall" selection
  const baseMeasureName =
    slices.length === 0 ? COMPARISON_OVERALL_SLICE_NAME : base?.name;
  const processBaseMeasureHint = (
    hint: Array<ISliceNameHint>,
    baseName: string,
  ): Array<ISliceNameHint> => {
    return hint.map((h) => {
      return {
        ...h,
        name: baseName,
        value: baseName,
      };
    });
  };
  if (base) {
    assertNotNil(baseMeasureName);
    ret.push({
      ...base,
      hint: processBaseMeasureHint(base.hint, baseMeasureName),
      isComparison: false,
      name: baseMeasureName,
      isNullSlice: false,
      originalSliceKey: base.key,
    });
  }
  if (cBase) {
    assertNotNil(baseMeasureName);
    ret.push(
      mapComparisonSlice(
        {
          ...cBase,
          name: baseMeasureName,
          hint: processBaseMeasureHint(cBase.hint, baseMeasureName),
        },
        future,
      ),
    );
  }

  const pairsByKey = getSlicePairsByKey(slices, cSlices);
  // preserve the order of the primary slices
  slices.forEach((s) => {
    const pair = getAndRemove(pairsByKey, s.key);
    assertNotNil(pair?.primary);
    ret.push({
      ...pair.primary,
      isComparison: false,
      isNullSlice: false,
      originalSliceKey: s.key,
    });
    const [comparison, isNullSlice] = pair?.comparison
      ? [pair.comparison, false]
      : [nullSlice(pair.primary), true];
    ret.push({
      ...mapComparisonSlice(comparison, future),
      isNullSlice,
    });
  });
  // any comparison slices not present in primary result - append at the end
  cSlices.forEach((s) => {
    const pair = getAndRemove(pairsByKey, s.key);
    if (pair) {
      // null means it was already added
      if (pair.primary !== null) {
        console.error('Unexpected non-null primary slice');
      }
      assertNotNil(pair.comparison);
      ret.push({
        ...nullSlice(pair.comparison),
        isComparison: false,
        isNullSlice: true,
        originalSliceKey: s.key,
      });
      ret.push(mapComparisonSlice(pair.comparison, future));
    }
  });
  return ret;
};

// Special case - inject "null" (0-value) columns into the mergedColumns (being built by the caller)
// which would correspond to all columns needed to satisfy the "null slice"
// XXX note that this function actually modifies some of its inputs vs returning a value
const produceNullSliceColumns = (
  columns: IExpressionColumnFieldsFragment[],
  cColumns: IExpressionColumnFieldsFragment[],
  mergedColumns: IComparisonMergeExpressionColumn[], // XXX modified by this function (appended to)
  mergedUnprocessedSlices: IMergedExpressionSlice[], // XXX modified by this function (via slice)
  slice: IMergedExpressionSlice, // XXX modified by this function (measure/variables column indexes)
  ix: number,
  future: boolean,
) => {
  // find the "other slice", coupled with this null slice - it is either the previous or the next
  const otherSlice = slice.isComparison
    ? mergedUnprocessedSlices[ix - 1]
    : mergedUnprocessedSlices[ix + 1];
  const lookupColumns = otherSlice.isComparison ? cColumns : columns;
  const newVariables: number[] = [];
  // find the pair slice columns and insert null-versions of these
  for (const variableIndex of otherSlice.variables) {
    const otherColumn = lookupColumns[variableIndex];
    newVariables.push(mergedColumns.length); // the index of the item we are about to insert
    const nullCol = nullColumn(otherColumn);
    const mappedNullCol = slice.isComparison
      ? mapComparisonColumn(nullCol, future)
      : nullCol;
    mergedColumns.push({
      ...mappedNullCol,
      originalIndex: null,
      originalSliceKey: slice.originalSliceKey,
      isComparison: slice.isComparison,
      isFutureComparison: future,
    });
  }
  // XXX we actually modify the input param here
  slice.variables = newVariables;
  slice.resultMeasureColumnIndex = mergedColumns.length;

  const resultColumn = getExpressionResultMeasureColumn(
    otherSlice,
    lookupColumns,
  );

  if (resultColumn) {
    const nullCol = nullColumn(resultColumn);
    const mappedNullCol = slice.isComparison
      ? mapComparisonColumn(nullCol, future)
      : nullCol;

    mergedColumns.push({
      ...mappedNullCol,
      originalIndex: null,
      originalSliceKey: slice.originalSliceKey,
      isComparison: slice.isComparison,
      isFutureComparison: future,
    });
  }
};

// Merge primary and comparison columns using already merged slice
// XXX This function modifies any (input) mergedUnprocessedSlices which have
// NULL_SLICE_COLUMN_INDEX slice.measure
const mergeColumns = (
  mergedUnprocessedSlices: IMergedExpressionSlice[],
  columns: IExpressionColumnFieldsFragment[],
  cColumns: IExpressionColumnFieldsFragment[],
  future: boolean,
) => {
  const mergedColumns: IComparisonMergeExpressionColumn[] = [];
  mergedUnprocessedSlices.forEach((slice, ix) => {
    if (slice.resultMeasureColumnIndex === NULL_SLICE_COLUMN_INDEX) {
      // XXX Special case - insert "null" columns into mergedColumns to satisfy the null slice display needs
      // also updates the invalid slice.measure and variables
      produceNullSliceColumns(
        columns,
        cColumns,
        mergedColumns,
        mergedUnprocessedSlices,
        slice,
        ix,
        future,
      );
    } else {
      // Append all implied by the slice variables columns and then the measure last. Note that this
      // logic must be in sync with parseMeasureColumnsHandler in transformExpressionData.ts.

      // which column list we will be indexing into for columns
      const lookupColumns = slice.isComparison ? cColumns : columns;
      for (const variableIndex of slice.variables) {
        // find the column and map it if needed
        const column = lookupColumns[variableIndex];
        const mappedColumn = slice.isComparison
          ? mapComparisonColumn(column, future)
          : column;
        // Push a copy of the column enhanced with its original index into the Kobol
        // data columns + an isComparison flag.
        // This value is then used to find the new index of a variable index originally
        // pointing to one of the two Kobol data columns
        mergedColumns.push({
          ...mappedColumn,
          originalIndex: variableIndex,
          originalSliceKey: slice.originalSliceKey,
          isComparison: slice.isComparison,
          isFutureComparison: future,
        });
      }

      // what we did for each variable, we do for the "result" column too.
      const resultColumn = getExpressionResultMeasureColumn(
        slice,
        lookupColumns,
      );

      if (resultColumn) {
        const mappedColumn = slice.isComparison
          ? mapComparisonColumn(resultColumn, future)
          : resultColumn;
        mergedColumns.push({
          ...mappedColumn,
          originalIndex: slice.resultMeasureColumnIndex,
          originalSliceKey: slice.originalSliceKey,
          isComparison: slice.isComparison,
          isFutureComparison: future,
        });
      }
    }
  });
  return mergedColumns;
};

// helper to update all slices measure and variables using a mapping function
// (oldIndex) => newIndex
const updateSingleSliceColumnIndexes = (
  slice: IMergedExpressionSlice,
  mapColumnIndex: (oldIx: number) => number,
): IMergedExpressionSlice => {
  return {
    ...slice,
    resultMeasureColumnIndex: !isNil(slice.resultMeasureColumnIndex)
      ? mapColumnIndex(slice.resultMeasureColumnIndex)
      : null,
    variables: slice.variables.map((v) => mapColumnIndex(v)),
  };
};

// update all slices measure+variables to point to the new merged columns. The matching columns are found
// by their orignalIndex value and their isComparison value (these two should be unique as a pair)
const updateSlicesColumnIndexes = (
  mergedColumns: IComparisonMergeExpressionColumn[],
  slices: IMergedExpressionSlice[],
): IMergedExpressionSlice[] => {
  return slices.map((s) => {
    const findNewIndex = (oldIx: number) =>
      s.isNullSlice
        ? oldIx // Null slices are special, they already have correct column indexes
        : mergedColumns.findIndex(
            (c) =>
              c.isComparison === s.isComparison && c.originalIndex === oldIx,
          );
    return updateSingleSliceColumnIndexes(s, findNewIndex);
  });
};

// Return "merged" expression data when the Expression analysis contains two data sets
// (primary nad comparison). In any case the columns merging logic has to be compatible
// with parseMeasureColumnsHandler in transformExpressionData.ts
// Note that the logic in this file could in theory be moved to Kobol.
export const mergeComparisonData = (
  data: IExpressionAnalysisData,
  comparisonData: IExpressionAnalysisData,
): IExpressionAnalysisData => {
  const {
    dates,
    base,
    columns,
    slices,
    // TODO?: statistics
  } = data;
  const {
    dates: cDates,
    base: cBase,
    columns: cColumns,
    slices: cSlices,
    // TODO?: statistics: cStatistics
  } = comparisonData;

  // keep track of whether we are dealing with past or future comparison
  const future = isFutureComparison(dates, cDates);

  // Merge the slices interleaving them (primary1, comparison1, primary2, comparison2, ...).
  // Some of these may be generated "null" slices (present in one but not the other data set).
  // The resulting list will have its variables/measure still pointing to indexes in the original
  // columns or cColumns arrays, and "null" slices will hae these pointing to special invalid value
  const mergedUnprocessedSlices = mergeSlices(
    base,
    cBase,
    slices,
    cSlices,
    future,
  );

  // merge the columns (this messes up the slices measure and variables - fixed later in updateSlicesColumnIndexes)
  // this also updates any null slices with actual indexes after inserting corresponding "null" columns
  const mergedColumns = mergeColumns(
    mergedUnprocessedSlices,
    columns,
    cColumns,
    future,
  );

  // update slice measure and variables with the new columns indexes
  const mergedSlices = updateSlicesColumnIndexes(
    mergedColumns,
    mergedUnprocessedSlices,
  );

  // if we have a base slice, drop the first slice from our merged list, and replace the base in the result
  let updatedBase = null;
  if (base) {
    updatedBase = mergedSlices.splice(0, 1)[0];
  }

  return {
    ...data,
    base: updatedBase,
    columns: mergedColumns,
    slices: mergedSlices,
  };
};
